import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { PhonePreview } from "@/components/common/PhonePreview";
import { Star } from "lucide-react";
import {
  useBookingCheckoutSettings,
  useUpdateBookingCheckoutSettings,
} from "./hooks/useBookingCheckoutSettings";
import { useCheckoutServiceLists } from "./hooks/useBookingCheckoutServiceLists";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useAllServices } from "@/hooks/useHotelServices";
import { SortableServiceItem } from "@/components/common/SortableServiceList";
import { HotelService } from "@/types/hotelService";
import catImage from "@/assets/cat.png";
import type {
  CheckoutSettingsResponse,
  PostCheckoutFeedbackSettings,
  UpdateCheckoutSettings,
} from "@/types/booking-checkout-settings";
import { ConfirmationInfoTab } from "./tabs/ConfirmationInfoTab";
import { UpsellTab } from "./tabs/UpsellTab";
import { BillSummaryTab } from "./tabs/BillSummaryTab";
import { ReviewsTab } from "./tabs/ReviewsTab";
import { FinalTab } from "./tabs/FinalTab";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface SelectedService {
  id: string;
  serviceId: string;
  serviceName: string;
  isNew?: boolean;
  isRemoved?: boolean;
}

enum CheckoutTabs {
  CONFIRMATION_INFO = "confirmation-info",
  UPSELL = "upsell",
  BILL_SUMMARY = "bill-summary",
  REVIEWS = "reviews",
  FINAL = "final",
}

const TAB_LABELS = {
  [CheckoutTabs.CONFIRMATION_INFO]: "Confirmation of info",
  [CheckoutTabs.UPSELL]: "Upsell",
  [CheckoutTabs.BILL_SUMMARY]: "Bill Summary",
  [CheckoutTabs.REVIEWS]: "Reviews",
  [CheckoutTabs.FINAL]: "Final",
};

const sanitizePostCheckoutFeedbackSettings = (
  postCheckoutFeedback: PostCheckoutFeedbackSettings,
): PostCheckoutFeedbackSettings => ({
  ...postCheckoutFeedback,
  reminders: postCheckoutFeedback.reminders.map((reminder) => ({
    order: reminder.order,
    sendAfterHours: reminder.sendAfterHours,
    condition: reminder.condition,
    isActive: reminder.isActive,
  })),
});

const buildCheckoutSettingsUpdatePayload = (
  settings: CheckoutSettingsResponse,
): UpdateCheckoutSettings => ({
  isLeaveReview: settings.isLeaveReview,
  isLeaveComment: settings.isLeaveComment,
  isSkipReview: settings.isSkipReview,
  isReviewCategories:
    settings.isReviewCategories ?? settings.reviewCategories.length > 0,
  isBillSummary: settings.isBillSummary,
  isSkipBill: settings.isSkipBill,
  isUpsellRecommendedServices: settings.isUpsellRecommendedServices,
  reviewCategories: settings.reviewCategories,
  ...(settings.postCheckoutFeedback
    ? {
        postCheckoutFeedback: sanitizePostCheckoutFeedbackSettings(
          settings.postCheckoutFeedback,
        ),
      }
    : {}),
});

const normalizeCheckoutSettings = (
  settings: CheckoutSettingsResponse,
): CheckoutSettingsResponse => ({
  ...settings,
  isReviewCategories:
    settings.isReviewCategories ?? settings.reviewCategories.length > 0,
});

export const CheckoutSettingsPage = () => {
  const [activeTab, setActiveTab] = useState<CheckoutTabs>(
    CheckoutTabs.CONFIRMATION_INFO,
  );
  const hotelId = useCurrentHotelId();
  const {
    checkoutRecommended,
    finalUpsell,
    updateCheckoutRecommended,
    updateFinalUpsell,
  } = useCheckoutServiceLists(hotelId || "");

  const { data: allServicesResponse } = useAllServices(hotelId || "");
  const allServices = allServicesResponse?.data || [];

  const { data: settings, isLoading } = useBookingCheckoutSettings(
    hotelId ?? undefined,
  );
  const updateMutation = useUpdateBookingCheckoutSettings();

  const [localSettings, setLocalSettings] =
    useState<CheckoutSettingsResponse | null>(null);
  const [recommendedServices, setRecommendedServices] = useState<
    SortableServiceItem[]
  >([]);
  const [upsellServices, setUpsellServices] = useState<SortableServiceItem[]>(
    [],
  );

  // Initialize local settings
  React.useEffect(() => {
    if (settings) {
      setLocalSettings(normalizeCheckoutSettings(settings));
    }
  }, [settings]);

  // Sync service lists
  React.useEffect(() => {
    if (checkoutRecommended.data) {
      setRecommendedServices(
        checkoutRecommended.data.map((s: HotelService) => ({
          id: s.id,
          serviceId: s.id,
          name: s.name,
        })),
      );
    }
  }, [checkoutRecommended.data]);

  React.useEffect(() => {
    if (finalUpsell.data) {
      setUpsellServices(
        finalUpsell.data.map((s: HotelService) => ({
          id: s.id,
          serviceId: s.id,
          name: s.name,
        })),
      );
    }
  }, [finalUpsell.data]);

  const handleSave = async () => {
    if (!localSettings) return;

    const updateData = buildCheckoutSettingsUpdatePayload(localSettings);

    updateMutation.mutate(updateData);

    if (hotelId) {
      updateCheckoutRecommended.mutate(
        recommendedServices.map((s) => s.serviceId),
      );
      updateFinalUpsell.mutate(upsellServices.map((s) => s.serviceId));
    }

    setActiveTab(CheckoutTabs.CONFIRMATION_INFO);
  };

  const handleNextStep = () => {
    const tabs = Object.values(CheckoutTabs);
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  const handlePreviousStep = () => {
    const tabs = Object.values(CheckoutTabs);
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
    }
  };

  if (isLoading) {
    return <div className="p-12">Loading...</div>;
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Main Content */}
      <div className="flex h-full flex-1 overflow-hidden">
        {/* Left Section - Settings */}
        <div className="flex h-full flex-1 flex-col overflow-y-auto p-6">
          <h1 className="text-2xl font-normal">
            Hotel check-out automation settings
          </h1>

          {/* Tab Navigation */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as CheckoutTabs)}
            className="mt-5 w-full"
          >
            <TabsList className="text-foreground h-9 w-full items-stretch gap-1 rounded-lg bg-[#FBFAFF] px-1 py-1.5 shadow-sm">
              {Object.entries(TAB_LABELS).map(([key, label]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="flex flex-1 items-center justify-center self-stretch rounded-md border border-transparent bg-transparent px-2 py-1 text-xs leading-none font-medium text-[#0A0A0A] shadow-none transition-colors hover:bg-[#EDE9FE]/40 data-[state=active]:border-[#C7BCFF] data-[state=active]:bg-[#F2F0FF] data-[state=active]:shadow-none"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Tab Content */}
          <div className="mt-6 pr-40 pl-10">
            {activeTab === CheckoutTabs.CONFIRMATION_INFO && (
              <ConfirmationInfoTab />
            )}
            {activeTab === CheckoutTabs.UPSELL && (
              <UpsellTab
                settings={localSettings}
                setSettings={setLocalSettings}
                recommendedServices={recommendedServices}
                setRecommendedServices={setRecommendedServices}
                allServices={allServices}
              />
            )}
            {activeTab === CheckoutTabs.BILL_SUMMARY && (
              <BillSummaryTab
                settings={localSettings}
                setSettings={setLocalSettings}
              />
            )}
            {activeTab === CheckoutTabs.REVIEWS && (
              <ReviewsTab
                settings={localSettings}
                setSettings={setLocalSettings}
              />
            )}
            {activeTab === CheckoutTabs.FINAL && (
              <FinalTab
                settings={localSettings}
                setSettings={setLocalSettings}
                upsellServices={upsellServices}
                setUpsellServices={setUpsellServices}
                allServices={allServices}
              />
            )}
          </div>

          <div className="mt-auto flex justify-center gap-4 p-6">
            {activeTab !== CheckoutTabs.CONFIRMATION_INFO && (
              <Button
                variant="new_outline"
                size="lg"
                onClick={handlePreviousStep}
              >
                Previous Step
              </Button>
            )}
            {activeTab !== CheckoutTabs.CONFIRMATION_INFO && (
              <Button variant="new_default" size="lg" onClick={handleSave}>
                Save
              </Button>
            )}
            {activeTab !== CheckoutTabs.FINAL && (
              <Button variant="new_outline" size="lg" onClick={handleNextStep}>
                Next Step
              </Button>
            )}
          </div>
        </div>

        {/* Right Section - Phone Preview */}
        <div className="flex w-[450px] flex-col items-center border-l bg-gray-50 p-6">
          <div className="mb-4 flex gap-2">
            <Button variant="new_outline" size="sm">
              Original
            </Button>
            <Button variant="new_outline" size="sm">
              Preview screen
            </Button>
          </div>
          <div className="mb-4 text-center">
            <h3 className="font-semibold">Guest App Preview</h3>
            <p className="text-xs text-gray-500">
              {activeTab === CheckoutTabs.CONFIRMATION_INFO && "Step 1/5"}
              {activeTab === CheckoutTabs.UPSELL && "Step 2/5"}
              {activeTab === CheckoutTabs.BILL_SUMMARY && "Step 3/5"}
              {activeTab === CheckoutTabs.REVIEWS && "Step 4/5"}
              {activeTab === CheckoutTabs.FINAL && "Step 5/5"}
            </p>
          </div>
          <PhonePreview
            title="Pre Check-Out"
            containerClassName="min-h-[650px]"
          >
            <div className="h-full space-y-4 overflow-y-auto bg-white p-4">
              {activeTab === CheckoutTabs.CONFIRMATION_INFO && (
                <div className="space-y-4">
                  <p className="text-center text-sm text-gray-500">Step 1/5</p>

                  {/* Cat placeholder image */}
                  <div className="flex items-center justify-center rounded-lg bg-gray-100 p-8">
                    <div className="text-6xl text-gray-400">
                      <img
                        src={catImage}
                        alt="Booking Placeholder"
                        className="max-h-32 object-contain mix-blend-multiply"
                      />
                    </div>
                  </div>

                  <h3 className="text-center font-semibold">
                    Check the information is correct
                  </h3>

                  <div className="space-y-2 text-sm">
                    <p className="font-medium">Mira Malkovich, Tom Malkovich</p>
                    <p className="text-gray-600">Booking dates</p>
                    <p>11.06.2025 - 14.06.2025</p>
                    <p className="text-gray-600">Room Type: Deluxe</p>
                    <p className="font-medium">Classic Room - #404</p>
                    <p className="text-gray-600">Check-Out Time</p>
                    <p>13:00</p>
                  </div>

                  <button className="mt-4 w-full rounded-lg bg-black py-3 text-white">
                    Next &gt;
                  </button>
                </div>
              )}

              {activeTab === CheckoutTabs.UPSELL && (
                <div className="space-y-4">
                  <p className="text-center text-sm text-gray-500">Step 2/5</p>

                  {/* Cat placeholder image */}
                  <div className="flex items-center justify-center rounded-lg bg-gray-100 p-8">
                    <div className="text-6xl text-gray-400">
                      <img
                        src={catImage}
                        alt="Services Placeholder"
                        className="max-h-32 object-contain mix-blend-multiply"
                      />
                    </div>
                  </div>

                  <h3 className="text-center font-semibold">
                    Additional services before departure
                  </h3>
                  <p className="text-center text-xs text-gray-600">
                    Select the services you consider booking for a more
                    comfortable departure from the hotel.
                  </p>

                  <div className="space-y-2">
                    {recommendedServices.map((service) => (
                      <div key={service.id}>
                        <div className="flex items-center justify-between py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {service.name}
                            </span>
                          </div>
                        </div>
                        <div className="h-px bg-gray-200" />
                      </div>
                    ))}
                    {recommendedServices.length === 0 && (
                      <p className="py-4 text-center text-sm text-gray-500">
                        No services selected
                      </p>
                    )}
                  </div>

                  <button className="mt-4 w-full rounded-lg bg-black py-3 text-white">
                    Next
                  </button>
                </div>
              )}

              {activeTab === CheckoutTabs.BILL_SUMMARY && (
                <div className="space-y-4">
                  <p className="text-center text-sm text-gray-500">Step 3/5</p>

                  <div className="rounded-lg bg-gray-50 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Total balance in USD
                      </span>
                      <select className="rounded border px-2 py-1 text-xs">
                        <option>USD</option>
                      </select>
                    </div>
                    <div className="text-3xl font-bold text-pink-600">
                      -$673,135
                    </div>
                  </div>

                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-semibold">
                        Total amount of expenses
                      </span>
                      <span className="text-sm font-semibold text-pink-600">
                        -$140,135
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded bg-gray-200"></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              Accommodation
                            </span>
                            <span className="text-sm">-400$</span>
                          </div>
                          <p className="text-xs text-gray-500">
                            11.06.2025 - 14.06.2025
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded bg-gray-200"></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              Services
                            </span>
                            <span className="text-sm">-92$</span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Dinner for some room x3, Relaxing massage x2
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded bg-gray-200"></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              Activities
                            </span>
                            <span className="text-sm">-40$</span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Leisure guide, Bicycle rent x2
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded bg-gray-200"></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              Taxes and fees
                            </span>
                            <span className="text-sm">50.455$</span>
                          </div>
                          <p className="text-xs text-gray-500">
                            25% for some example taxes
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button className="flex w-full items-center justify-center gap-2 rounded-lg bg-black py-3 text-white">
                    <span className="h-5 w-5 rounded-full bg-white"></span>
                    Pay
                  </button>
                  {localSettings?.isSkipBill && (
                    <button className="w-full rounded-lg border border-black py-3 text-black">
                      Skip
                    </button>
                  )}
                </div>
              )}

              {activeTab === CheckoutTabs.REVIEWS && (
                <div className="space-y-4">
                  <p className="text-center text-sm text-gray-500">Step 4/5</p>

                  {/* Cat placeholder image */}
                  <div className="flex items-center justify-center rounded-lg bg-gray-100 p-8">
                    <div className="text-6xl text-gray-400">
                      <img
                        src={catImage}
                        alt="Review Placeholder"
                        className="max-h-32 object-contain mix-blend-multiply"
                      />
                    </div>
                  </div>

                  <h3 className="text-center font-semibold">
                    Please leave an honest review
                  </h3>

                  {/* Overall Rating */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-8 w-8 ${star <= 4 ? "fill-orange-400 text-orange-400" : "fill-gray-300 text-gray-300"} `}
                        />
                      ))}
                    </div>
                    <div className="flex w-full justify-between px-4 text-xs text-gray-500">
                      <span>Bad</span>
                      <span>Very Good</span>
                    </div>
                  </div>

                  {localSettings?.isReviewCategories &&
                    localSettings.reviewCategories.length > 0 && (
                      <div className="space-y-3">
                        {localSettings.reviewCategories.map((category) => (
                          <div key={category} className="space-y-1">
                            <p className="text-sm font-medium">{category}</p>
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className="h-6 w-6 fill-gray-300 text-gray-300"
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  {localSettings?.isLeaveComment && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Additional comments
                      </label>
                      <div className="min-h-24 rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-400">
                        Share anything else about your stay...
                      </div>
                    </div>
                  )}

                  <button className="mt-4 w-full rounded-lg bg-black py-3 text-white">
                    Send Review
                  </button>
                  {localSettings?.isSkipReview && (
                    <button className="w-full rounded-lg border border-black py-3 text-black">
                      Skip
                    </button>
                  )}
                </div>
              )}

              {activeTab === CheckoutTabs.FINAL && (
                <div className="space-y-4">
                  <p className="text-center text-sm text-gray-500">Step 5/5</p>

                  <div className="flex items-center justify-center rounded-lg bg-gray-100 p-8">
                    <div className="text-6xl text-gray-400">
                      {/* Placeholder for Final Step */}
                      <img
                        src={catImage}
                        alt="Final Step"
                        className="max-h-32 object-contain mix-blend-multiply"
                      />
                    </div>
                  </div>

                  <h3 className="text-center font-semibold">
                    Thank you for your visit! We look forward to seeing you
                    again.
                  </h3>

                  <div className="space-y-2">
                    <p className="text-sm font-medium">Recommended actions:</p>
                    {upsellServices.map((service) => (
                      <div key={service.id}>
                        <div className="flex items-center justify-between py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {service.name}
                            </span>
                          </div>
                        </div>
                        <div className="h-px bg-gray-200" />
                      </div>
                    ))}
                  </div>

                  <button className="mt-4 w-full rounded-lg border border-black py-3 text-black">
                    Room
                  </button>
                </div>
              )}
            </div>
          </PhonePreview>
        </div>
      </div>
    </div>
  );
};
